// src/pages/Directory.js
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Star, ChevronDown, Sparkles, X, ArrowRight, RotateCcw, Heart } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { SESSION_FORMATS } from '../data/directoryConstants';
import { PARENT_CATEGORIES, ALL_MODALITIES } from '../data/modalityTaxonomy';
import { mapPractitionerDocToDirectoryCard } from '../utils/practitionerDirectoryMapper';
import { discoverPractitioners, refinePractitioners } from '../services/seeker-discovery-service';
import BadgeDisplay from '../components/shared/BadgeDisplay';
import BadgeExplainer from '../components/shared/BadgeExplainer';
import { trackEvent } from '../utils/analytics';

// FilterDropdown Component
const FilterDropdown = ({ label, options, selectedValues, onChange, showClearAll = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const selectedCount = selectedValues.length;

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '10px 16px',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          background: selectedCount > 0 ? '#F5F3FF' : '#FFFFFF',
          color: selectedCount > 0 ? '#7c3aed' : '#374151',
          fontSize: '14px',
          fontWeight: selectedCount > 0 ? '600' : '400',
          fontFamily: 'Montserrat, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
        {selectedCount > 0 && (
          <span style={{
            background: '#7c3aed',
            color: 'white',
            borderRadius: '12px',
            padding: '2px 8px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {selectedCount}
          </span>
        )}
        <ChevronDown size={16} style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.2s'
        }} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          zIndex: 100,
          minWidth: '220px',
          maxHeight: '320px',
          overflowY: 'auto'
        }}>
          {showClearAll && selectedCount > 0 && (
            <>
              <button
                onClick={handleClearAll}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  color: '#7c3aed',
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: 'Montserrat, sans-serif',
                  cursor: 'pointer',
                  transition: 'color 0.15s ease'
                }}
                onMouseEnter={(e) => e.target.style.color = '#6D28D9'}
                onMouseLeave={(e) => e.target.style.color = '#7c3aed'}
              >
                Clear All
              </button>
              <div style={{ height: '1px', background: '#E5E7EB', margin: '4px 0' }} />
            </>
          )}

          {options.map((option) => (
            <label
              key={option}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 16px',
                cursor: 'pointer',
                transition: 'background 0.2s',
                background: 'transparent'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option)}
                onChange={() => handleToggle(option)}
                style={{
                  width: '16px',
                  height: '16px',
                  marginRight: '10px',
                  cursor: 'pointer',
                  accentColor: '#7c3aed'
                }}
              />
              <span style={{
                fontSize: '14px',
                color: '#374151',
                fontFamily: 'Montserrat, sans-serif'
              }}>
                {option}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// Returns display names for a practitioner's modalities (new or legacy format)
const getModalityDisplayNames = (practitioner) => {
  const m = practitioner.modalities;
  if (m && typeof m === 'object' && !Array.isArray(m) && Array.isArray(m.selected) && m.selected.length > 0) {
    return m.selected.map(x => x.name);
  }
  if (Array.isArray(m) && m.length > 0) return m;
  return practitioner.practiceTypes || [];
};

// Two-tier Modality Filter Dropdown — parent categories + specific modality search
const ModalityFilterDropdown = ({ selectedParentCategories, selectedSpecificModalityIds, onChangeParents, onChangeSpecific }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [specificSearch, setSpecificSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Clear search when dropdown closes
  useEffect(() => {
    if (!isOpen) setSpecificSearch('');
  }, [isOpen]);

  const totalSelected = selectedParentCategories.length + selectedSpecificModalityIds.length;

  const handleToggleParent = (parentId) => {
    if (selectedParentCategories.includes(parentId)) {
      onChangeParents(selectedParentCategories.filter(id => id !== parentId));
    } else {
      onChangeParents([...selectedParentCategories, parentId]);
    }
  };

  const handleToggleSpecific = (modalityId) => {
    if (selectedSpecificModalityIds.includes(modalityId)) {
      onChangeSpecific(selectedSpecificModalityIds.filter(id => id !== modalityId));
    } else {
      onChangeSpecific([...selectedSpecificModalityIds, modalityId]);
    }
  };

  const handleClearAll = () => {
    onChangeParents([]);
    onChangeSpecific([]);
  };

  const filteredModalities = specificSearch.trim()
    ? ALL_MODALITIES.filter(m => m.name.toLowerCase().includes(specificSearch.toLowerCase()))
    : [];

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '10px 16px',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          background: totalSelected > 0 ? '#F5F3FF' : '#FFFFFF',
          color: totalSelected > 0 ? '#7c3aed' : '#374151',
          fontSize: '14px',
          fontWeight: totalSelected > 0 ? '600' : '400',
          fontFamily: 'Montserrat, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          whiteSpace: 'nowrap',
        }}
      >
        Modality
        {totalSelected > 0 && (
          <span style={{
            background: '#7c3aed',
            color: 'white',
            borderRadius: '12px',
            padding: '2px 8px',
            fontSize: '12px',
            fontWeight: '600',
          }}>
            {totalSelected}
          </span>
        )}
        <ChevronDown size={16} style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.2s',
        }} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          zIndex: 100,
          minWidth: '280px',
          maxHeight: '440px',
          overflowY: 'auto',
        }}>
          {/* Clear all */}
          {totalSelected > 0 && (
            <>
              <button
                onClick={handleClearAll}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  color: '#7c3aed',
                  fontSize: '13px',
                  fontWeight: '600',
                  fontFamily: 'Montserrat, sans-serif',
                  cursor: 'pointer',
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={(e) => e.target.style.color = '#6D28D9'}
                onMouseLeave={(e) => e.target.style.color = '#7c3aed'}
              >
                Clear All ({totalSelected})
              </button>
              <div style={{ height: '1px', background: '#E5E7EB', margin: '0 0 4px' }} />
            </>
          )}

          {/* Section label */}
          <div style={{
            padding: '8px 16px 4px',
            fontSize: '10px',
            fontWeight: '700',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#9CA3AF',
            fontFamily: 'Montserrat, sans-serif',
          }}>
            Practice Area
          </div>

          {/* Parent category checkboxes */}
          {PARENT_CATEGORIES.map((cat) => (
            <label
              key={cat.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '9px 16px',
                cursor: 'pointer',
                background: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <input
                type="checkbox"
                checked={selectedParentCategories.includes(cat.id)}
                onChange={() => handleToggleParent(cat.id)}
                style={{
                  width: '16px',
                  height: '16px',
                  marginRight: '10px',
                  cursor: 'pointer',
                  accentColor: '#7c3aed',
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '15px', marginRight: '8px', lineHeight: 1 }}>{cat.icon}</span>
              <span style={{
                flex: 1,
                fontSize: '13px',
                color: '#374151',
                fontFamily: 'Montserrat, sans-serif',
              }}>
                {cat.name}
              </span>
              <span style={{
                fontSize: '11px',
                color: '#9CA3AF',
                fontFamily: 'Montserrat, sans-serif',
                marginLeft: '6px',
              }}>
                {cat.count}
              </span>
            </label>
          ))}

          {/* Divider + specific modality search */}
          <div style={{ height: '1px', background: '#E5E7EB', margin: '6px 0' }} />
          <div style={{ padding: '8px 12px 6px' }}>
            <input
              type="text"
              placeholder="Search specific modality..."
              value={specificSearch}
              onChange={(e) => setSpecificSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '13px',
                fontFamily: 'Montserrat, sans-serif',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box',
                color: '#374151',
              }}
              onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          {/* Specific modality search results */}
          {filteredModalities.length > 0 && (
            filteredModalities.map((m) => (
              <label
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  background: 'transparent',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F9FAFB'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <input
                  type="checkbox"
                  checked={selectedSpecificModalityIds.includes(m.id)}
                  onChange={() => handleToggleSpecific(m.id)}
                  style={{
                    width: '16px',
                    height: '16px',
                    marginRight: '10px',
                    cursor: 'pointer',
                    accentColor: '#7c3aed',
                    flexShrink: 0,
                  }}
                />
                <span style={{
                  flex: 1,
                  fontSize: '13px',
                  color: '#374151',
                  fontFamily: 'Montserrat, sans-serif',
                }}>
                  {m.name}
                </span>
                <span style={{
                  fontSize: '11px',
                  color: '#9CA3AF',
                  fontFamily: 'Montserrat, sans-serif',
                  marginLeft: '6px',
                  whiteSpace: 'nowrap',
                }}>
                  {m.parentName.split(' & ')[0]}
                </span>
              </label>
            ))
          )}

          {specificSearch.trim() && filteredModalities.length === 0 && (
            <p style={{
              padding: '4px 16px 10px',
              fontSize: '12px',
              color: '#9CA3AF',
              fontFamily: 'Montserrat, sans-serif',
              margin: 0,
            }}>
              No modalities found
            </p>
          )}

          {!specificSearch.trim() && (
            <p style={{
              padding: '2px 16px 10px',
              fontSize: '11px',
              color: '#C4B5FD',
              fontFamily: 'Montserrat, sans-serif',
              margin: 0,
            }}>
              Type to search for a specific modality
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// PractitionerCard Component
const PractitionerCard = ({ practitioner, isSaved, onToggleSave, showHeart }) => {
  const navigate = useNavigate();

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  const handleCardClick = () => {
    navigate(`/site/${practitioner.slug}`);
  };

  return (
    <div
      onClick={handleCardClick}
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.15)';
        e.currentTarget.style.borderColor = '#C4B5FD';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.borderColor = '#E5E7EB';
      }}
    >
      <div style={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}>
        {/* Favorite heart */}
        {showHeart && (
          <span
            role="button" tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onToggleSave?.(practitioner.id, practitioner); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onToggleSave?.(practitioner.id, practitioner); } }}
            style={{
              position: 'absolute', top: '16px', right: '16px', cursor: 'pointer',
              userSelect: 'none', zIndex: 2, padding: '4px',
            }}
          >
            <Heart size={18} fill={isSaved ? '#bb9749' : 'none'} stroke={isSaved ? '#bb9749' : '#D1D5DB'} strokeWidth={2} />
          </span>
        )}
        {/* Header with Photo/Initials and Info */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '14px' }}>
          {/* Photo/Initials */}
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #7c3aed, #22d3ee)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <span style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#FFFFFF',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              {getInitials(practitioner.displayName)}
            </span>
          </div>

          {/* Name and Rating */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#111827',
              fontFamily: 'Playfair Display, serif',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {practitioner.displayName}
              </span>
              <BadgeDisplay verification={practitioner.verification} size="sm" />
            </h3>

            {/* Rating — "New" treatment when the practitioner has no reviews yet */}
            {practitioner.hasReviews && practitioner.rating != null ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Star size={14} fill="#F59E0B" color="#F59E0B" />
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827',
                  fontFamily: 'Montserrat, sans-serif'
                }}>
                  {practitioner.rating.toFixed(1)}
                </span>
                <span style={{
                  fontSize: '13px',
                  color: '#6B7280',
                  fontFamily: 'Montserrat, sans-serif'
                }}>
                  ({practitioner.reviewCount})
                </span>
              </div>
            ) : (
              <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '12px',
                backgroundColor: '#F5F3FF',
                color: '#7c3aed',
                fontSize: '11px',
                fontWeight: '600',
                fontFamily: 'Montserrat, sans-serif',
              }}>
                New
              </span>
            )}
          </div>
        </div>

        {/* Tagline */}
        <p style={{
          fontSize: '14px',
          color: '#6B7280',
          fontFamily: 'Montserrat, sans-serif',
          marginBottom: '12px',
          fontStyle: 'italic',
          lineHeight: '1.5'
        }}>
          "{practitioner.tagline}"
        </p>

        {/* Modality Tags */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          marginBottom: '14px'
        }}>
          {getModalityDisplayNames(practitioner).slice(0, 3).map((name) => (
            <span
              key={name}
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                backgroundColor: '#F3F4F6',
                color: '#4B5563',
                fontSize: '11px',
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: '500',
              }}
            >
              {name}
            </span>
          ))}
        </div>

        {/* Spacer — pushes everything below to the bottom */}
        <div style={{ flex: 1 }} />

        {/* Bottom section: Location, Format, Price */}
        <div style={{
          paddingTop: '14px',
          borderTop: '1px solid #F3F4F6',
        }}>
          {/* Location and Session Format Tags */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexWrap: 'wrap',
            marginBottom: '10px'
          }}>
            <span style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '12px',
              color: '#6B7280',
            }}>
              📍 {practitioner.location || 'Virtual'}
            </span>
            {practitioner.sessionFormats.length > 0 && (
              <span style={{ color: '#D1D5DB' }}>·</span>
            )}
            {practitioner.sessionFormats.map(format => (
              <span
                key={format}
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: format === 'Virtual' ? '#EFF6FF' : '#FEF3C7',
                  color: format === 'Virtual' ? '#2563EB' : '#92400E',
                  fontSize: '11px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: '500',
                }}
              >
                {format}
              </span>
            ))}
          </div>

          {/* Price (hidden when the practitioner has no priced services) */}
          {practitioner.priceRange && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#111827',
                fontFamily: 'Montserrat, sans-serif'
              }}>
                ${practitioner.priceRange.min}–${practitioner.priceRange.max}
              </span>
            </div>
          )}
        </div>

        {/* Primary CTA — always at very bottom. Routes to the practitioner's
            published site (same as clicking the card). Gold per public-page convention. */}
        <div style={{ marginTop: '14px' }}>
          <button
            onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: '#bb9749',
              border: 'none',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: 'Montserrat, sans-serif',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#a8863f'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#bb9749'}
          >
            Book a session
          </button>
        </div>
      </div>
    </div>
  );
};

// AI Discovery Result Card
const AIResultCard = ({ rec, navigate }) => {
  const matchColor =
    rec.matchScore >= 85 ? '#059669' :
    rec.matchScore >= 65 ? '#D97706' : '#7c3aed';

  return (
    <div
      onClick={() => navigate(`/site/${rec.slug}`)}
      style={{
        background: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '12px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#7c3aed';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(124,58,237,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#E5E7EB';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Match score badge */}
      <div style={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        background: matchColor,
        color: '#fff',
        fontSize: '11px',
        fontWeight: '700',
        padding: '3px 8px',
        borderRadius: '20px',
        fontFamily: 'Montserrat, sans-serif',
      }}>
        {rec.matchScore}% match
      </div>

      {/* Name */}
      <p style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: '16px',
        fontWeight: '700',
        color: '#111827',
        margin: '0 0 4px',
        paddingRight: '72px',
      }}>
        {rec.displayName}
      </p>

      {/* Location + formats */}
      {(rec.location || rec.sessionFormats?.length > 0) && (
        <p style={{
          fontSize: '12px',
          color: '#9CA3AF',
          fontFamily: 'Montserrat, sans-serif',
          margin: '0 0 10px',
        }}>
          {[rec.location, rec.sessionFormats?.join(' · ')].filter(Boolean).join(' · ')}
        </p>
      )}

      {/* Modalities */}
      {rec.modalities?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
          {rec.modalities.slice(0, 4).map((m) => (
            <span
              key={m}
              style={{
                fontSize: '11px',
                background: '#F5F3FF',
                color: '#7c3aed',
                padding: '2px 8px',
                borderRadius: '20px',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: '600',
              }}
            >
              {m}
            </span>
          ))}
        </div>
      )}

      {/* AI match reason */}
      <p style={{
        fontSize: '13px',
        color: '#374151',
        fontFamily: 'Montserrat, sans-serif',
        lineHeight: '1.5',
        margin: '0 0 10px',
      }}>
        {rec.matchReason}
      </p>

      {/* Highlighted service */}
      {rec.highlightedService && (
        <p style={{
          fontSize: '12px',
          color: '#7c3aed',
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: '600',
          margin: '0',
        }}>
          ✦ {rec.highlightedService}
        </p>
      )}

      {/* Rating */}
      {rec.rating && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '8px',
        }}>
          <Star size={12} fill="#F59E0B" color="#F59E0B" />
          <span style={{ fontSize: '12px', color: '#6B7280', fontFamily: 'Montserrat, sans-serif' }}>
            {rec.rating.toFixed(1)} ({rec.reviewCount})
          </span>
        </div>
      )}
    </div>
  );
};

// AI Seeker Discovery Search Section
const AIDiscoverySection = ({ navigate }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [refinement, setRefinement] = useState('');
  const [refineLoading, setRefineLoading] = useState(false);
  const [originalQuery, setOriginalQuery] = useState('');
  const textareaRef = useRef(null);

  const EXAMPLE_PROMPTS = [
    "I need help processing grief and finding peace",
    "Something feels off in my home's energy",
    "I want to understand my birth chart and life purpose",
    "Going through a major career transition",
    "I'd like to connect with my spirit guides",
  ];

  const handleSearch = useCallback(async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setOriginalQuery(query.trim());
    setRefinement('');
    try {
      const res = await discoverPractitioners(query.trim());
      setResult(res);
      trackEvent('directory_search', { query_type: 'ai' });
    } catch (err) {
      if (err.message === 'rate_limited') {
        setError("You've made several searches recently. Please wait a few minutes and try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [query, loading]);

  const handleRefine = useCallback(async () => {
    if (!refinement.trim() || refineLoading) return;
    setRefineLoading(true);
    setError(null);
    try {
      const res = await refinePractitioners(originalQuery, result, refinement.trim());
      setResult(res);
      setRefinement('');
    } catch (err) {
      if (err.message === 'rate_limited') {
        setError("You've made several searches recently. Please wait a few minutes and try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setRefineLoading(false);
    }
  }, [refinement, refineLoading, originalQuery, result]);

  const handleReset = () => {
    setQuery('');
    setResult(null);
    setError(null);
    setRefinement('');
    setOriginalQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleRefineKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleRefine();
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)',
      backgroundSize: '400% 400%',
      animation: 'auroraShift 18s ease infinite',
      padding: '48px 24px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Dark overlay for readability */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(15, 10, 30, 0.3)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: '720px', margin: '0 auto', position: 'relative' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(167,139,250,0.15)',
            border: '1px solid rgba(167,139,250,0.3)',
            borderRadius: '20px',
            padding: '6px 14px',
            marginBottom: '16px',
          }}>
            <Sparkles size={14} color="#A78BFA" />
            <span style={{
              fontSize: '12px',
              fontWeight: '700',
              color: '#A78BFA',
              fontFamily: 'Montserrat, sans-serif',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}>
              AI Seeker Guide
            </span>
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '28px',
            fontWeight: '700',
            color: '#FFFFFF',
            margin: '0 0 10px',
            lineHeight: '1.3',
          }}>
            Describe what you're seeking
          </h2>
          <p style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
            color: 'rgba(255,255,255,0.6)',
            margin: 0,
          }}>
            Tell us in your own words — our AI will find the right practitioner for you
          </p>
        </div>

        {/* Search box */}
        {!result && (
          <>
            <div style={{ position: 'relative', marginBottom: '16px' }}>
              <textarea
                ref={textareaRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. I'm going through a difficult divorce and need spiritual support and clarity..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: '15px',
                  fontFamily: 'Montserrat, sans-serif',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '12px',
                  color: '#FFFFFF',
                  resize: 'none',
                  outline: 'none',
                  boxSizing: 'border-box',
                  lineHeight: '1.5',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.target.style.borderColor = 'rgba(167,139,250,0.6)'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
              />
            </div>

            {/* Example prompt chips */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '20px',
            }}>
              {EXAMPLE_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  onClick={() => setQuery(prompt)}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '20px',
                    color: 'rgba(255,255,255,0.7)',
                    fontSize: '12px',
                    fontFamily: 'Montserrat, sans-serif',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(167,139,250,0.2)';
                    e.currentTarget.style.borderColor = 'rgba(167,139,250,0.4)';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
                    e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                  }}
                >
                  {prompt}
                </button>
              ))}
            </div>

            <button
              onClick={handleSearch}
              disabled={!query.trim() || loading}
              style={{
                width: '100%',
                padding: '14px',
                background: query.trim() && !loading ? '#7c3aed' : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '10px',
                color: '#FFFFFF',
                fontSize: '15px',
                fontWeight: '600',
                fontFamily: 'Montserrat, sans-serif',
                cursor: query.trim() && !loading ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                if (query.trim() && !loading) e.currentTarget.style.background = '#6D28D9';
              }}
              onMouseLeave={(e) => {
                if (query.trim() && !loading) e.currentTarget.style.background = '#7c3aed';
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  Finding your match...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Find My Practitioner
                </>
              )}
            </button>
          </>
        )}

        {/* Error state */}
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.15)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '10px',
            padding: '14px 18px',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            <span style={{ fontSize: '14px', color: '#FCA5A5', fontFamily: 'Montserrat, sans-serif' }}>
              {error}
            </span>
            <button
              onClick={handleReset}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#FCA5A5', cursor: 'pointer' }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Results */}
        {result && (
          <div>
            {/* Result header */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: '20px',
              gap: '12px',
            }}>
              <div>
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.5)',
                  margin: '0 0 4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: '600',
                }}>
                  AI understood your request as:
                </p>
                <p style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '16px',
                  color: '#E9D5FF',
                  margin: 0,
                  lineHeight: '1.4',
                  fontStyle: 'italic',
                }}>
                  "{result.seekerSummary}"
                </p>
              </div>
              <button
                onClick={handleReset}
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px',
                  padding: '7px 12px',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '12px',
                  fontFamily: 'Montserrat, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: '600',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                  e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
                }}
              >
                <RotateCcw size={12} />
                New Search
              </button>
            </div>

            {/* Result cards */}
            {result.recommendations.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                {result.recommendations.map((rec) => (
                  <AIResultCard key={rec.practitionerId} rec={rec} navigate={navigate} />
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px 24px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '12px',
                marginBottom: '24px',
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔮</div>
                <p style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.6)',
                  margin: 0,
                }}>
                  No practitioners found for this search. Try describing your needs differently, or browse the full directory below.
                </p>
              </div>
            )}

            {/* Follow-up refinement */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '16px',
            }}>
              <p style={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '12px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.5)',
                margin: '0 0 10px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}>
                Refine your results
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  value={refinement}
                  onChange={(e) => setRefinement(e.target.value)}
                  onKeyDown={handleRefineKeyDown}
                  placeholder="e.g. I prefer someone virtual, budget under $100..."
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    fontSize: '14px',
                    fontFamily: 'Montserrat, sans-serif',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'rgba(167,139,250,0.5)'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                />
                <button
                  onClick={handleRefine}
                  disabled={!refinement.trim() || refineLoading}
                  style={{
                    padding: '10px 16px',
                    background: refinement.trim() && !refineLoading ? '#7c3aed' : 'rgba(255,255,255,0.08)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    cursor: refinement.trim() && !refineLoading ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    if (refinement.trim() && !refineLoading) e.currentTarget.style.background = '#6D28D9';
                  }}
                  onMouseLeave={(e) => {
                    if (refinement.trim() && !refineLoading) e.currentTarget.style.background = '#7c3aed';
                  }}
                >
                  {refineLoading ? (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }} />
                  ) : (
                    <ArrowRight size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Directory Component
const DirectoryEventsTab = React.lazy(() => import('../components/directory/DirectoryEventsTab'));

// ── Fuzzy search helpers ──

function levenshtein(a, b) {
  const m = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) m[0][i] = i;
  for (let j = 0; j <= b.length; j++) m[j][0] = j;
  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      m[j][i] = Math.min(m[j][i - 1] + 1, m[j - 1][i] + 1, m[j - 1][i - 1] + cost);
    }
  }
  return m[b.length][a.length];
}

function fuzzyMatch(search, target) {
  const s = search.toLowerCase().trim();
  const t = target.toLowerCase().trim();
  if (t.includes(s)) return true;
  const sWords = s.split(/\s+/);
  const tWords = t.split(/\s+/);
  return sWords.every(sw =>
    tWords.some(tw => {
      if (tw.includes(sw) || sw.includes(tw)) return true;
      const maxDist = sw.length >= 4 ? 2 : 1;
      return levenshtein(sw, tw) <= maxDist;
    })
  );
}

// Bounded fetch — filtering/sorting stays client-side (§5/§8 of the wiring
// findings), so we pull the full eligible set up to this cap.
const DIRECTORY_FETCH_LIMIT = 200;

const Directory = () => {
  const navigate = useNavigate();
  const [directoryTab, setDirectoryTab] = useState('practitioners');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParentCategories, setSelectedParentCategories] = useState([]);

  // Badge explainer modal
  const [showBadgeExplainer, setShowBadgeExplainer] = useState(false);

  // Live practitioner directory (real Firestore data)
  const [practitioners, setPractitioners] = useState([]);
  const [practitionersLoading, setPractitionersLoading] = useState(true);

  // Favorites for any logged-in user
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  // Fetch the live, eligible practitioner set once on mount.
  // Visibility gate (PR1 product decisions):
  //   • approved-only — verification.manualReviewStatus === 'approved' (Firestore
  //     equality filter; uses the automatic single-field index, no composite needed)
  //   • has a published slug (client-side; absent ⇒ never published)
  //   • directoryVisible !== false (client-side; absent ⇒ visible — forward-compatible
  //     with the Settings toggle added in PR2)
  //   • resolvable displayName (client-side completeness guard)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!db) {
        if (!cancelled) setPractitionersLoading(false);
        return;
      }
      try {
        const q = query(
          collection(db, 'practitioners'),
          where('verification.manualReviewStatus', '==', 'approved'),
          limit(DIRECTORY_FETCH_LIMIT)
        );
        const snap = await getDocs(q);
        const cards = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (!data.slug) return; // not published
          if (data.directoryVisible === false) return; // opted out
          const card = mapPractitionerDocToDirectoryCard(docSnap.id, data);
          if (!card.displayName) return; // incomplete profile
          cards.push(card);
        });
        if (!cancelled) setPractitioners(cards);
      } catch (err) {
        console.error('Directory: failed to load practitioners', err);
        if (!cancelled) setPractitioners([]);
      } finally {
        if (!cancelled) setPractitionersLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoggedIn(true);
        const functions = getFunctions(undefined, 'us-west1');
        const fn = httpsCallable(functions, 'getSavedPractitioners');
        fn({}).then(result => {
          setSavedIds(new Set((result.data.saved || []).map(p => p.practitionerId)));
        }).catch(() => {});
      } else {
        setIsLoggedIn(false);
      }
    });
    return () => unsub();
  }, []);

  const handleToggleSave = async (practitionerId, practitioner) => {
    const functions = getFunctions(undefined, 'us-west1');
    if (savedIds.has(practitionerId)) {
      setSavedIds(prev => { const next = new Set(prev); next.delete(practitionerId); return next; });
      httpsCallable(functions, 'unsavePractitioner')({ practitionerId }).catch(() => {
        setSavedIds(prev => { const next = new Set(prev); next.add(practitionerId); return next; });
      });
    } else {
      setSavedIds(prev => { const next = new Set(prev); next.add(practitionerId); return next; });
      httpsCallable(functions, 'savePractitioner')({
        practitionerId,
        name: practitioner?.displayName || 'Practitioner',
        photo: practitioner?.photo || null,
        slug: practitioner?.slug || practitionerId,
      }).catch(() => {
        setSavedIds(prev => { const next = new Set(prev); next.delete(practitionerId); return next; });
      });
    }
  };
  const [selectedSpecificModalityIds, setSelectedSpecificModalityIds] = useState([]);
  const [selectedFormats, setSelectedFormats] = useState([]);
  const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
  const [sortBy, setSortBy] = useState('rating'); // 'rating' (default), 'price_low', 'price_high', 'reviews'

  const priceRangeOptions = ['Under $75', '$75–$125', '$125–$175', 'Over $175'];

  // Filtering and Sorting Logic
  const filteredAndSortedPractitioners = useMemo(() => {
    let results = [...practitioners];

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim();
      results = results.filter(p => {
        if (fuzzyMatch(q, p.displayName)) return true;
        if (fuzzyMatch(q, p.location)) return true;
        if (fuzzyMatch(q, p.tagline)) return true;
        if (p.bio && fuzzyMatch(q, p.bio)) return true;
        // Check modalities (new structured format)
        const m = p.modalities;
        if (m && typeof m === 'object' && !Array.isArray(m)) {
          if (Array.isArray(m.selected) && m.selected.some(mod => fuzzyMatch(q, mod.name))) return true;
          if (Array.isArray(m.custom) && m.custom.some(mod => fuzzyMatch(q, mod.name))) return true;
        }
        // Fallback: legacy practiceTypes
        if (p.practiceTypes && p.practiceTypes.some(type => fuzzyMatch(q, type))) return true;
        if (p.services && p.services.some(s => fuzzyMatch(q, s.name))) return true;
        return false;
      });
    }

    // Parent category filter — matches any practitioner whose parentCategories includes a selected category
    if (selectedParentCategories.length > 0) {
      results = results.filter(p => {
        const m = p.modalities;
        if (m?.parentCategories?.length > 0) {
          return selectedParentCategories.some(cat => m.parentCategories.includes(cat));
        }
        return false;
      });
    }

    // Specific modality filter — matches any practitioner with that exact modality in their selected list
    if (selectedSpecificModalityIds.length > 0) {
      results = results.filter(p => {
        const m = p.modalities;
        if (Array.isArray(m?.selected) && m.selected.length > 0) {
          return selectedSpecificModalityIds.some(id => m.selected.some(x => x.id === id));
        }
        return false;
      });
    }

    // Format filter - updated for array
    if (selectedFormats.length > 0) {
      results = results.filter(p =>
        selectedFormats.some(f => p.sessionFormats.includes(f))
      );
    }

    // Price Range filter
    if (selectedPriceRanges.length > 0) {
      results = results.filter(p => {
        if (!p.priceRange) return false; // no priced services → excluded from price filters
        const min = p.priceRange.min;
        const max = p.priceRange.max;

        return selectedPriceRanges.some(range => {
          if (range === 'Under $75') return min < 75;
          if (range === '$75–$125') return (min >= 75 && min <= 125) || (max >= 75 && max <= 125) || (min < 75 && max > 125);
          if (range === '$125–$175') return (min >= 125 && min <= 175) || (max >= 125 && max <= 175) || (min < 125 && max > 175);
          if (range === 'Over $175') return max > 175;
          return false;
        });
      });
    }

    // Sorting (priceRange may be null when a practitioner has no priced services)
    switch (sortBy) {
      case 'rating':
        // Default sort: rating desc, with reviewCount as the tiebreak.
        results.sort((a, b) => {
          const r = (b.rating || 0) - (a.rating || 0);
          if (r !== 0) return r;
          return (b.reviewCount || 0) - (a.reviewCount || 0);
        });
        break;
      case 'price_low':
        results.sort((a, b) =>
          (a.priceRange ? a.priceRange.min : Infinity) - (b.priceRange ? b.priceRange.min : Infinity)
        );
        break;
      case 'price_high':
        results.sort((a, b) =>
          (b.priceRange ? b.priceRange.max : -Infinity) - (a.priceRange ? a.priceRange.max : -Infinity)
        );
        break;
      case 'reviews':
        results.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
        break;
      default:
        break;
    }

    return results;
  }, [practitioners, searchQuery, selectedParentCategories, selectedSpecificModalityIds, selectedFormats, selectedPriceRanges, sortBy]);

  const hasActiveFilters = searchQuery.trim() || selectedParentCategories.length > 0 || selectedSpecificModalityIds.length > 0 || selectedFormats.length > 0 || selectedPriceRanges.length > 0;

  return (
    <>
    <Helmet>
      <title>Find Your Practitioner — Divine Frameworks</title>
      <meta name="description" content="Browse trusted spiritual and wellness practitioners. Find tarot readers, energy healers, astrologers, and more on Divine Frameworks." />
      <meta property="og:title" content="Find Your Practitioner — Divine Frameworks" />
      <meta property="og:description" content="Browse trusted spiritual and wellness practitioners on Divine Frameworks." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://divineframeworks.com/directory" />
      <meta property="og:site_name" content="Divine Frameworks" />
    </Helmet>
    <div style={{
      minHeight: '100vh',
      background: '#F9FAFB',
      fontFamily: 'Montserrat, sans-serif'
    }}>
      {/* AI Seeker Discovery — top of directory */}
      <AIDiscoverySection navigate={navigate} />

      {/* Hero Section with Search */}
      <div style={{
        background: 'linear-gradient(180deg, #FAFAF9 0%, #F5F0EB 100%)',
        padding: '0',
        textAlign: 'center',
        borderBottom: '1px solid #E8E0D8',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Purple accent bar at the very top — thin, bold, branded */}
        <div style={{
          height: '4px',
          background: 'linear-gradient(90deg, #7c3aed, #22d3ee, #7c3aed)',
        }} />

        {/* Content area */}
        <div style={{ padding: '48px 24px 44px' }}>

          {/* Small label above heading */}
          <p style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '11px',
            fontWeight: '700',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: '#7c3aed',
            marginBottom: '12px',
          }}>
            Divine Frameworks Directory
          </p>

          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '36px',
            fontWeight: '700',
            color: '#1F2937',
            marginBottom: '8px',
          }}>
            Find Your Practitioner
          </h1>

          {/* Decorative divider under heading — small, centered, purple/gold */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '14px',
          }}>
            <div style={{ width: '24px', height: '1.5px', backgroundColor: '#7c3aed' }} />
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: '#bb9749',
            }} />
            <div style={{ width: '24px', height: '1.5px', backgroundColor: '#7c3aed' }} />
          </div>

          <p style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '15px',
            color: '#6B7280',
            maxWidth: '460px',
            margin: '0 auto 28px',
            lineHeight: '1.5',
          }}>
            Connect with trusted practitioners for guidance, healing, and transformation
          </p>

          {/* Search bar with purple search button */}
          <div style={{
            maxWidth: '560px',
            margin: '0 auto',
            position: 'relative',
          }}>
            <input
              type="text"
              placeholder="Search by name, practice type, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 52px 14px 20px',
                fontSize: '15px',
                fontFamily: "'Montserrat', sans-serif",
                border: '1px solid #D1D5DB',
                borderRadius: '50px',
                outline: 'none',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                boxSizing: 'border-box',
                backgroundColor: '#FFFFFF',
                color: '#1F2937',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#7c3aed';
                e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)';
              }}
            />
            {/* Purple search button — circular, right side of input */}
            <button
              type="button"
              style={{
                position: 'absolute',
                right: '5px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '34px',
                height: '34px',
                minWidth: '34px',
                minHeight: '34px',
                borderRadius: '50%',
                backgroundColor: '#7c3aed',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: '0',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6D28D9'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: 'block' }}
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Directory Tabs: Practitioners | Events */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', gap: 0 }}>
          {[
            { key: 'practitioners', label: 'Practitioners' },
            { key: 'events', label: 'Events' },
          ].map(tab => (
            <span
              key={tab.key}
              role="button"
              tabIndex={0}
              onClick={() => setDirectoryTab(tab.key)}
              onKeyDown={(e) => e.key === 'Enter' && setDirectoryTab(tab.key)}
              style={{
                padding: '14px 24px', fontSize: 15, fontWeight: 600,
                fontFamily: 'Montserrat, sans-serif', cursor: 'pointer', userSelect: 'none',
                color: directoryTab === tab.key ? '#7c3aed' : '#6B7280',
                borderBottom: directoryTab === tab.key ? '3px solid #7c3aed' : '3px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </span>
          ))}
        </div>
      </div>

      {/* Events Tab */}
      {directoryTab === 'events' && (
        <React.Suspense fallback={<div style={{ textAlign: 'center', padding: '80px 20px' }}><p style={{ color: '#6B7280', fontSize: 14, fontFamily: 'Montserrat, sans-serif' }}>Loading events...</p></div>}>
          <DirectoryEventsTab />
        </React.Suspense>
      )}

      {/* Practitioners Tab — existing content below */}
      {directoryTab !== 'practitioners' ? null : <>

      {/* Filter Bar */}
      <div style={{
        background: '#F9FAFB',
        borderBottom: '1px solid #E5E7EB',
        padding: '20px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center'
        }}>
          <ModalityFilterDropdown
            selectedParentCategories={selectedParentCategories}
            selectedSpecificModalityIds={selectedSpecificModalityIds}
            onChangeParents={setSelectedParentCategories}
            onChangeSpecific={setSelectedSpecificModalityIds}
          />

          <FilterDropdown
            label="Format"
            options={SESSION_FORMATS}
            selectedValues={selectedFormats}
            onChange={setSelectedFormats}
          />

          <FilterDropdown
            label="Price Range"
            options={priceRangeOptions}
            selectedValues={selectedPriceRanges}
            onChange={setSelectedPriceRanges}
          />

          {/* Sort Dropdown */}
          <div style={{ marginLeft: 'auto' }}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '10px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                background: '#FFFFFF',
                color: '#374151',
                fontSize: '14px',
                fontFamily: 'Montserrat, sans-serif',
                cursor: 'pointer',
                appearance: 'none',
                paddingRight: '36px',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center'
              }}
            >
              <option value="rating">Highest Rated</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="reviews">Most Reviews</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '24px 24px 16px'
      }}>
        <p style={{
          fontSize: '14px',
          color: '#6B7280',
          fontFamily: 'Montserrat, sans-serif'
        }}>
          {practitionersLoading
            ? 'Loading…'
            : `${filteredAndSortedPractitioners.length} practitioner${filteredAndSortedPractitioners.length !== 1 ? 's' : ''} found`}
        </p>
        <span
          role="button"
          tabIndex={0}
          onClick={() => setShowBadgeExplainer(true)}
          onKeyDown={(e) => e.key === 'Enter' && setShowBadgeExplainer(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontSize: '13px', fontWeight: 600, color: '#7c3aed',
            cursor: 'pointer', userSelect: 'none',
            padding: '6px 14px', borderRadius: '8px', background: '#f3eeff',
            fontFamily: 'Montserrat, sans-serif',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#ede9fe'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#f3eeff'}
        >
          🛡️ What do badges mean?
        </span>
      </div>

      {/* Practitioner Grid */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px 60px'
      }}>
        {practitionersLoading ? (
          // Loading State
          <div style={{
            textAlign: 'center',
            padding: '80px 24px',
            color: '#6B7280',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '14px',
          }}>
            Loading practitioners…
          </div>
        ) : filteredAndSortedPractitioners.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '24px'
          }}>
            {filteredAndSortedPractitioners.map((practitioner) => (
              <PractitionerCard key={practitioner.id} practitioner={practitioner} showHeart={isLoggedIn} isSaved={savedIds.has(practitioner.id)} onToggleSave={handleToggleSave} />
            ))}
          </div>
        ) : (
          // Empty State
          <div style={{
            textAlign: 'center',
            padding: '80px 24px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔮</div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827',
              fontFamily: 'Playfair Display, serif',
              marginBottom: '8px'
            }}>
              No practitioners found
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              fontFamily: 'Montserrat, sans-serif',
              marginBottom: '24px'
            }}>
              {hasActiveFilters
                ? 'Try adjusting your filters or search terms'
                : 'No practitioners available at this time'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedParentCategories([]);
                  setSelectedSpecificModalityIds([]);
                  setSelectedFormats([]);
                  setSelectedPriceRanges([]);
                }}
                style={{
                  padding: '10px 24px',
                  background: '#7c3aed',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'Montserrat, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#6d28d9'}
                onMouseLeave={(e) => e.target.style.background = '#7c3aed'}
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      </>}

      {/* Badge Explainer Modal */}
      <BadgeExplainer isOpen={showBadgeExplainer} onClose={() => setShowBadgeExplainer(false)} />
    </div>
    </>
  );
};

export default Directory;
